import React from 'react';
import { Alert, Box, CircularProgress, Container, Typography } from '@mui/material';
import { MAIN_BG_COLOR } from '../../theme';

type LegalPdfPageProps = {
    title: string;
    pdfUrl: string;
    iframeTitle: string;
};

const LegalPdfPage: React.FC<LegalPdfPageProps> = ({ title, pdfUrl, iframeTitle }) => {
    const [pdfLoading, setPdfLoading] = React.useState(true);
    const [pdfError, setPdfError] = React.useState(false);

    return (
        <Box
            sx={{
                bgcolor: MAIN_BG_COLOR,
                minHeight: { xs: 'calc(100dvh - 56px)', md: 'calc(100dvh - 64px)' },
                py: { xs: 3, sm: 4 },
                boxSizing: 'border-box',
            }}
        >
            <Container maxWidth="lg">
                <Typography
                    variant="h3"
                    component="h1"
                    sx={{
                        fontWeight: 700,
                        fontFamily: '"DM Serif Display", serif',
                        color: 'white',
                        fontSize: { xs: '2rem', sm: '2.375rem' },
                        lineHeight: 1.15,
                        mb: 1,
                    }}
                >
                    {title}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.55)', mb: 3 }}>
                    Última actualización: 1 de Octubre de 2025
                </Typography>

                <Box sx={{ position: 'relative' }}>
                    {pdfLoading && !pdfError && (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                minHeight: 400,
                                position: 'absolute',
                                inset: 0,
                                zIndex: 1,
                            }}
                        >
                            <CircularProgress sx={{ color: 'white' }} />
                        </Box>
                    )}

                    {pdfError ? (
                        <Alert
                            severity="error"
                            sx={{
                                bgcolor: 'rgba(244, 67, 54, 0.15)',
                                color: '#ffcdd2',
                                border: '1px solid rgba(239, 83, 80, 0.45)',
                                '& .MuiAlert-icon': { color: '#ef9a9a' },
                            }}
                        >
                            No se pudo cargar el documento PDF. Por favor, intenta nuevamente más
                            tarde.
                        </Alert>
                    ) : (
                        <Box
                            component="iframe"
                            src={pdfUrl}
                            sx={{
                                width: '100%',
                                height: '80vh',
                                minHeight: 600,
                                border: '1px solid rgba(255,255,255,0.14)',
                                borderRadius: 1,
                                bgcolor: '#fff',
                            }}
                            onLoad={() => setPdfLoading(false)}
                            onError={() => {
                                setPdfLoading(false);
                                setPdfError(true);
                            }}
                            title={iframeTitle}
                        />
                    )}
                </Box>

                <Typography
                    sx={{
                        mt: 3,
                        textAlign: 'center',
                        color: 'rgba(255,255,255,0.55)',
                    }}
                >
                    Si tienes problemas para ver el documento, puedes{' '}
                    <Box
                        component="a"
                        href={pdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                            color: '#90caf9',
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' },
                        }}
                    >
                        descargarlo aquí
                    </Box>
                </Typography>
            </Container>
        </Box>
    );
};

export default LegalPdfPage;
